import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Loader2, Check, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// LinkedIn Conversion Tracking Helper
const trackLinkedInConversion = () => {
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: 28515713 });
  }
};

// Password strength validation
const checkPasswordStrength = (password) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
  
  const passed = Object.values(checks).filter(Boolean).length;
  const strength = passed === 4 ? 'strong' : passed >= 2 ? 'medium' : 'weak';
  const isValid = checks.minLength && checks.hasUppercase && checks.hasLowercase && checks.hasNumber;
  
  return { checks, strength, isValid, passed };
};

const PasswordStrengthIndicator = ({ password }) => {
  const { checks, strength, passed } = useMemo(() => checkPasswordStrength(password), [password]);
  
  if (!password) return null;
  
  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500'
  };
  
  const requirements = [
    { key: 'minLength', label: 'At least 8 characters', met: checks.minLength },
    { key: 'hasUppercase', label: 'One uppercase letter', met: checks.hasUppercase },
    { key: 'hasLowercase', label: 'One lowercase letter', met: checks.hasLowercase },
    { key: 'hasNumber', label: 'One number', met: checks.hasNumber },
  ];
  
  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      {/* Strength bar */}
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= passed ? strengthColors[strength] : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
      
      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        {requirements.map(({ key, label, met }) => (
          <div key={key} className={`flex items-center gap-1 ${met ? 'text-green-600' : 'text-slate-400'}`}>
            {met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firmName: '',
    fullName: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const passwordStrength = useMemo(() => checkPasswordStrength(formData.password), [formData.password]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password, confirmPassword, firmName, fullName } = formData;
    
    if (!email || !password || !firmName || !fullName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!passwordStrength.isValid) {
      toast.error('Password does not meet requirements');
      return;
    }
    
    setLoading(true);
    try {
      await register(email, password, firmName, fullName);
      trackLinkedInConversion(); // Track successful registration
      toast.success('Account created successfully!');
      navigate('/setup'); // Redirect to setup wizard for new users
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle 
              className="text-2xl font-bold text-center"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Create your account
            </CardTitle>
            <CardDescription className="text-center">
              Start your 7-day free trial
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="h-11 bg-slate-50"
                  data-testid="register-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="firmName">Law Firm Name</Label>
                <Input
                  id="firmName"
                  name="firmName"
                  type="text"
                  placeholder="Smith & Associates"
                  value={formData.firmName}
                  onChange={handleChange}
                  className="h-11 bg-slate-50"
                  data-testid="register-firmname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="attorney@lawfirm.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-11 bg-slate-50"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-11 bg-slate-50"
                  data-testid="register-password-input"
                />
                <PasswordStrengthIndicator password={formData.password} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="h-11 bg-slate-50"
                  data-testid="register-confirm-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-slate-500">Already have an account? </span>
              <Link 
                to="/login" 
                className="text-teal-600 hover:text-teal-700 font-medium"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
